import React, { useState, useEffect, useRef } from 'react';
import styles from '../pages/Resume.module.scss';

const SCROLL_MARGIN = 24; // должно совпадать со scroll-margin-top у .section

function SectionNav({ sections }) {
    const [activeId, setActiveId] = useState(sections[0]?.id);
    // Пока идёт клик-навигация, scroll-spy не вмешивается — иначе подсветка
    // «пробегает» промежуточные разделы и кажется, что всё скачет.
    const animatingRef = useRef(false);

    useEffect(() => {
        const updateActive = () => {
            if (animatingRef.current) return;

            const probe = window.scrollY + window.innerHeight * 0.3;
            let current = sections[0]?.id;

            for (const s of sections) {
                const el = document.getElementById(s.id);
                if (el) {
                    const top = el.getBoundingClientRect().top + window.scrollY;
                    if (top <= probe) current = s.id;
                }
            }

            const atBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 4;
            if (atBottom) current = sections[sections.length - 1]?.id;

            setActiveId(current);
        };

        updateActive();
        window.addEventListener('scroll', updateActive, { passive: true });
        window.addEventListener('resize', updateActive);
        return () => {
            window.removeEventListener('scroll', updateActive);
            window.removeEventListener('resize', updateActive);
        };
    }, [sections]);

    const smoothScrollTo = (targetY) => {
        const startY = window.scrollY;
        const diff = targetY - startY;
        if (Math.abs(diff) < 2) return;

        // Уважаем системную настройку «уменьшить движение»
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            window.scrollTo(0, targetY);
            return;
        }

        // Контролируемая длительность вместо резкого нативного рывка
        const duration = Math.min(700, Math.max(400, Math.abs(diff) * 0.35));
        const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

        let startTs = null;
        animatingRef.current = true;

        const step = (ts) => {
            if (startTs === null) startTs = ts;
            const t = Math.min((ts - startTs) / duration, 1);
            window.scrollTo(0, startY + diff * easeInOutCubic(t));
            if (t < 1) {
                requestAnimationFrame(step);
            } else {
                animatingRef.current = false;
            }
        };
        requestAnimationFrame(step);
    };

    const handleClick = (e, id) => {
        e.preventDefault();
        const el = document.getElementById(id);
        if (!el) return;

        setActiveId(id); // сразу подсвечиваем выбранный — без «пробегания» по разделам
        const targetY = el.getBoundingClientRect().top + window.scrollY - SCROLL_MARGIN;
        smoothScrollTo(targetY);
    };

    return (
        <nav className={styles.sectionNav} aria-label="Section navigation">
            <ul>
                {sections.map((s) => (
                    <li
                        key={s.id}
                        className={activeId === s.id ? styles.sectionNavActive : ''}
                    >
                        <a href={`#${s.id}`} onClick={(e) => handleClick(e, s.id)}>
                            <span className={styles.sectionNavLine}></span>
                            <span className={styles.sectionNavLabel}>{s.label}</span>
                        </a>
                    </li>
                ))}
            </ul>
        </nav>
    );
}

export default SectionNav;
